import { createContext, useContext } from "react";
import { CallState, Experiment, ExperimentState } from "redmond";

type DefaultVariantNames = "control" | "available";
type LDOnlyConfig<Id extends string, VariantName = DefaultVariantNames> = {
  id: Id;
  variant: VariantName;
};

type LDOnlyJsonConfig<
  Id extends string,
  Datum extends object,
  VariantName = DefaultVariantNames
> = LDOnlyConfig<Id, VariantName> & {
  data: Datum;
};

export type UntypedExperiment = {
  id: string;
  variant: string;
};

export type ActiveFlagsResponse = {
  experiments: UntypedExperiment[];
  trackingProperties: Record<string, string>;
};

export type CorpExperimentsMap = {
  "corp-traveler-maintenance": LDOnlyJsonConfig<
    "corp-traveler-maintenance",
    {
      title: string;
      subtext: string;
      severity: "error" | "warning" | "info" | "success";
    },
    "banner" | "page" | "control"
  >;
  "corp-admin-activation-flow": LDOnlyConfig<"corp-admin-activation-flow">;
  "corp-admin-policy-descriptors": LDOnlyConfig<"corp-admin-policy-descriptors">;
  "corp-traveler-hotel-itemized-receipt": LDOnlyConfig<"corp-traveler-hotel-itemized-receipt">;
  "c1-fintech-pricefreeze": LDOnlyConfig<"c1-fintech-pricefreeze">;
  "corp-traveler-on-behalf-of": LDOnlyConfig<"corp-traveler-on-behalf-of">;
  "corp-dynamic-limits": LDOnlyConfig<"corp-dynamic-limits">; // Repurposed for showing the corporate debugging pannel
  "corp-traveler-onboarding": LDOnlyConfig<"corp-traveler-onboarding">;
  "c1-corporate-credit-card-validation": LDOnlyConfig<
    "c1-corporate-credit-card-validation",
    "control" | "any-spark"
  >;
  "corp-custom-header-logo-dbc": LDOnlyConfig<"corp-custom-header-logo-dbc">;
  "corp-hide-price-drop-protection": LDOnlyConfig<"corp-hide-price-drop-protection">;
  "corp-ca-1383": LDOnlyConfig<"corp-ca-1383">;
  "corp-hide-price-freeze": LDOnlyConfig<"corp-hide-price-freeze">;
  "corp-amadeus-multiroom": LDOnlyConfig<"corp-amadeus-multiroom">;
  "c1-marketplace-trees": LDOnlyConfig<"c1-marketplace-trees">;
  "c1-marketplace-travel-wallet-offers": LDOnlyConfig<"c1-marketplace-travel-wallet-offers">;
  "c1-marketplace-credit-offer-stacking-v1": LDOnlyConfig<"c1-marketplace-credit-offer-stacking-v1">;
  "c1-marketplace-credit-transaction-history": LDOnlyConfig<"c1-marketplace-credit-transaction-history">;
  "c1-marketplace-travel-wallet-credits": LDOnlyConfig<"c1-marketplace-travel-wallet-credits">;
  "corp-hide-travel-wallet-offers": LDOnlyConfig<"corp-hide-travel-wallet-offers">;
  "c1-marketplace-HotelsCaliforniaBill644": LDOnlyConfig<"c1-marketplace-HotelsCaliforniaBill644">;
  "c1-marketplace-HotelsCaliforniaBill537": LDOnlyConfig<
    "c1-marketplace-HotelsCaliforniaBill537",
    "control" | "least-disruption"
  >;
  "b2b-agency-air-multicity": LDOnlyConfig<"b2b-agency-air-multicity">;
  "corp-sean-ellis-question": LDOnlyConfig<
    "corp-sean-ellis-question",
    "control" | "available" | "debug"
  >;
  "c1-fintech-hotel-cfar": LDOnlyConfig<"c1-fintech-hotel-cfar">;
  "corp-trip-purpose": LDOnlyConfig<"corp-trip-purpose">;
  "corp-fintech-subscription-smoketest": LDOnlyConfig<
    "corp-fintech-subscription-smoketest",
    | "control"
    | "available_variant1"
    | "available_variant2"
    | "available_variant3"
  >;
  "c1-fintech-cfar-hotel-continue-checkout-text-enable": LDOnlyConfig<
    "c1-fintech-cfar-hotel-continue-checkout-text-enable",
    "control" | "available"
  >;
  "corp-non-financial-users": LDOnlyConfig<"corp-non-financial-users">;
  "c1-marketplace-global-mobile-nav": LDOnlyConfig<"c1-marketplace-global-mobile-nav">;
  "corp-vxb-eligibility": LDOnlyConfig<"corp-vxb-eligibility">;
  "corp-flight-checkout-redesign": LDOnlyConfig<"corp-flight-checkout-redesign">;
  "corp-nfu-inproduct-awareness": LDOnlyConfig<
    "corp-nfu-inproduct-awareness",
    "control" | "available" | "debug"
  >;
  "corp-onboarding-revamp": LDOnlyConfig<
    "corp-onboarding-revamp",
    "control" | "available" | "debug"
  >;
  "corp-multiaccount-recently-viewed": LDOnlyConfig<"corp-multiaccount-recently-viewed">;
  "c1-corporate-spark-migration-banner": LDOnlyConfig<
    "c1-corporate-spark-migration-banner",
    "control" | "available" | "debug" | "test-launch-date"
  >;
  "corp-force-cfar-error-modal": LDOnlyConfig<
    "corp-force-cfar-error-modal",
    "control" | "available"
  >;
  "corp-force-fda-error-modal": LDOnlyConfig<
    "corp-force-fda-error-modal",
    "control" | "available"
  >;
  "corp-hotel-book-again": LDOnlyConfig<
    "corp-hotel-book-again",
    "control" | "available"
  >;
  "corp-checkout-rewards-earn-banner": LDOnlyConfig<
    "corp-checkout-rewards-earn-banner",
    "control" | "available" | "flights-earn"
  >;
  "corp-hotel-room-rates-design-test-v1": LDOnlyConfig<
    "corp-hotel-room-rates-design-test-v1",
    "control" | "available"
  >;
};

export type CorpBusinessExperimentsMap = {
  "corp-intra-business-referrals": LDOnlyConfig<
    "corp-intra-business-referrals",
    "control" | "available" | "earn-banner"
  >;
};

// Default variants
export const CONTROL = "control";
export const AVAILABLE = "available";
export const VARIANT_1 = "variant-1";
export const VARIANT_2 = "variant-2";

export const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
export type DefaultVariantType = typeof CONTROL | typeof AVAILABLE;

// readonly (together with const restriction below) ensures typeof supportedVariants[number]
// can be recognized by transpiler as an array of constants
// [string, ...string[]] ensures array is not empty.
export function getExperimentVariantCustomVariants<
  T extends readonly [string, ...string[]]
>(
  experiments: Array<Experiment>,
  experimentId: string,
  // List of supported variants. If the variant retreived is not recognized by the code, the code will fall back to the first variant.
  // Note the input list needs to be declared as const e.g. const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
  supportedVariants: T
): (typeof supportedVariants)[number] {
  const experiment = experiments?.find((exp) => {
    if (exp.id === experimentId) {
      return exp;
    }
    return null;
  });

  if (experiment) {
    if (supportedVariants.includes(experiment.variant)) {
      return experiment.variant;
    }
    return supportedVariants[0];
  }
  return supportedVariants[0];
}

export function getExperimentVariant(
  experiments: Array<Experiment>,
  experimentId: string
): string {
  const experiment = experiments?.find((exp) => {
    if (exp.id === experimentId) {
      return exp;
    }

    return null;
  });

  if (experiment) {
    return experiment.variant;
  }
  return CONTROL;
}

export function getDefaultExperimentVariant(
  experiments: Array<Experiment>,
  experimentId: string
): DefaultVariantType {
  return getExperimentVariantCustomVariants(
    experiments,
    experimentId,
    DEFAULT_VARIANTS
  );
}

interface ExperimentStateWithCallState extends ExperimentState {
  callState: CallState;
}

export const ExperimentsContext = createContext<
  ExperimentStateWithCallState | undefined
>(undefined);

export function useExperimentsContext(): ExperimentStateWithCallState {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) throw new Error(`must be used within a ExperimentsProvider`);
  return ctx;
}

// CapOne experiments
export const LUXURY_COMPLIMENTARY_NIGHT_PROMOTION =
  "c1-luxury-complimentary-night-promotion";
